import React, { useCallback, useMemo, useState } from "react";
import { FinancialDocumentListItem } from "@common/types/financialDocument";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import type { DateOnlyRange } from "@design-system/DatePicker/DatePicker.types";
import type { DisableableTrigger } from "@design-system/overlay/triggerButtonProps";
import { useUpdatePeriodMutation } from "@mutations/financialDocument";

interface Props {
    financialDocument: FinancialDocumentListItem;
    children: DisableableTrigger;
    /** Controls the popup when given; `onOpenChange` still reports every request. */
    open?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    /** Called after the new period saves. */
    onSaved?: () => void;
    /** The popup's accessible name (a grid column's name). */
    popupLabel?: string;
}

export const DocumentPeriodSelector: React.FC<Readonly<Props>> = ({
    financialDocument,
    children,
    open,
    onOpenChange,
    onSaved,
    popupLabel,
}) => {
    const updatePeriod = useUpdatePeriodMutation();
    const { taxonomyExtraction } = financialDocument;

    // Holds the half-open range while the user is mid-selection (first day
    // clicked, second pending). Only complete ranges (or clears) are committed.
    const [draftRange, setDraftRange] = useState<DateOnlyRange | undefined>(
        undefined,
    );

    const committedValue = useMemo((): DateOnlyRange | undefined => {
        const from = taxonomyExtraction?.periodStartDate ?? undefined;
        const to = taxonomyExtraction?.periodEndDate ?? undefined;
        if (!from && !to) {
            return undefined;
        }
        return { from, to };
    }, [
        taxonomyExtraction?.periodStartDate,
        taxonomyExtraction?.periodEndDate,
    ]);

    const handleChange = useCallback(
        (next: DateOnlyRange | undefined) => {
            if (next?.from && !next.to) {
                setDraftRange(next);
                return;
            }
            setDraftRange(undefined);

            const periodStartDate = next?.from ?? null;
            const periodEndDate = next?.to ?? null;

            if (
                periodStartDate ===
                    (taxonomyExtraction?.periodStartDate ?? null) &&
                periodEndDate === (taxonomyExtraction?.periodEndDate ?? null)
            ) {
                return;
            }

            updatePeriod.mutate(
                { financialDocument, periodStartDate, periodEndDate },
                { onSuccess: onSaved },
            );
        },
        [updatePeriod, financialDocument, taxonomyExtraction, onSaved],
    );

    return (
        <DatePicker
            mode="range"
            variant="button"
            value={draftRange ?? committedValue}
            onValueChange={handleChange}
            open={open}
            onOpenChange={(isOpen) => {
                setDraftRange(undefined);
                onOpenChange?.(isOpen);
            }}
            clearable
            showTriggerIcon={false}
            numberOfMonths={2}
            placeholder="Set period"
            popupLabel={popupLabel}
            trigger={children}
        />
    );
};
